import React, { useState } from 'react';
import { Sparkles, CheckCircle, AlertCircle } from 'lucide-react';
import { categorizeTransactions } from '../utils/openai';
import { Transaction } from '../types';

interface BulkTransactionImportProps {
  onTransactionsAdded: (transactions: Transaction[]) => void;
  onClose: () => void;
}

interface CategorizedTransaction {
  transaction: string;
  category: string;
  confidence: number;
  parsed: {
    amount: number;
    type: 'income' | 'expense';
    description: string;
  };
}

export const BulkTransactionImport: React.FC<BulkTransactionImportProps> = ({
  onTransactionsAdded,
  onClose,
}) => {
  const [transactions, setTransactions] = useState<string>('');
  const [categorizedTransactions, setCategorizedTransactions] = useState<CategorizedTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const parseTransaction = (transactionText: string): { amount: number; type: 'income' | 'expense'; description: string } => {
    // Remove common prefixes and clean up the text
    const cleanText = transactionText
      .replace(/^\d+\.\s*/, '') // Remove numbering
      .replace(/^\$\s*/, '') // Remove leading dollar sign
      .trim();

    // Try to extract amount and description
    const amountMatch = cleanText.match(/^([\d,]+\.?\d*)/);
    if (amountMatch) {
      const amount = parseFloat(amountMatch[1].replace(/,/g, ''));
      const description = cleanText.replace(/^[\d,]+\.?\d*\s*/, '').trim();
      
      // Determine if it's income or expense based on keywords
      const incomeKeywords = ['from', 'salary', 'payment', 'deposit', 'refund', 'return'];
      const isIncome = incomeKeywords.some(keyword => 
        description.toLowerCase().includes(keyword)
      );

      return {
        amount: Math.abs(amount),
        type: isIncome ? 'income' : 'expense',
        description: description || 'Unknown transaction',
      };
    }

    return {
      amount: 0,
      type: 'expense',
      description: cleanText || 'Unknown transaction',
    };
  };

  const handleCategorize = async () => {
    if (!transactions.trim()) {
      setError('Please enter some transactions to categorize');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Split transactions by newlines and filter out empty lines
      const transactionList = transactions
        .split('\n')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      if (transactionList.length === 0) {
        setError('No valid transactions found');
        return;
      }

      // Categorize transactions using OpenAI
      const result = await categorizeTransactions(transactionList);

      // Parse and combine the results
      const categorized = result.categorizations.map(cat => ({
        transaction: cat.transaction,
        category: cat.category,
        confidence: cat.confidence,
        parsed: parseTransaction(cat.transaction),
      }));

      setCategorizedTransactions(categorized);
    } catch (err) {
      setError('Failed to categorize transactions. Please check your OpenAI API key and try again.');
      console.error('Categorization error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransactions = () => {
    const newTransactions: Transaction[] = categorizedTransactions.map((cat, index) => ({
      id: `bulk-${Date.now()}-${index}`,
      user_id: 'user1',
      amount: cat.parsed.type === 'expense' ? -cat.parsed.amount : cat.parsed.amount,
      type: cat.parsed.type,
      category: cat.category,
      description: cat.parsed.description,
      classification: 'personal',
      date: new Date().toISOString().split('T')[0],
      currency: 'USD',
      created_at: new Date().toISOString(),
    }));

    onTransactionsAdded(newTransactions);
    onClose();
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return 'text-success-600';
    if (confidence >= 0.8) return 'text-warning-600';
    return 'text-danger-600';
  };

  const getConfidenceIcon = (confidence: number) => {
    if (confidence >= 0.9) return <CheckCircle className="w-4 h-4" />;
    if (confidence >= 0.8) return <AlertCircle className="w-4 h-4" />;
    return <AlertCircle className="w-4 h-4" />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Bulk Import Transactions</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Paste your transactions and let AI categorize them automatically
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Input Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Transactions (one per line)
            </label>
            <textarea
              value={transactions}
              onChange={(e) => setTransactions(e.target.value)}
              placeholder="Paste your transactions here, one per line. For example:&#10;$75 at Save-On-Foods&#10;$1300 from ACME Rent&#10;$27 at Shell Gas Station"
              className="input-field h-32 resize-none"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Format: $amount at/from description
            </p>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Categorize Button */}
          <button
            onClick={handleCategorize}
            disabled={loading || !transactions.trim()}
            className="btn-primary flex items-center justify-center w-full"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Categorize with AI
              </>
            )}
          </button>

          {/* Results Section */}
          {categorizedTransactions.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Categorized Transactions
              </h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {categorizedTransactions.map((cat, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {cat.parsed.description}
                      </p>
                      <p className="text-sm text-gray-600">
                        {cat.parsed.type === 'income' ? '+' : '-'}
                        {cat.parsed.amount.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {cat.category}
                        </p>
                        <div className="flex items-center space-x-1">
                          {getConfidenceIcon(cat.confidence)}
                          <span className={`text-xs ${getConfidenceColor(cat.confidence)}`}>
                            {(cat.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex space-x-3">
                <button
                  onClick={handleAddTransactions}
                  className="btn-primary flex-1"
                >
                  Add All Transactions
                </button>
                <button
                  onClick={onClose}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 